import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, MessageCircle, Sparkles, Award, ExternalLink } from "lucide-react";
import { waLink, WA_MESSAGES } from "@/lib/whatsapp";

const DHRUVA_IMG = "/dhruva.jpg";
const BEFORE_AFTER = "/before-after.png";

const CREDENTIALS = [
  "3rd Generation Yoga Practitioner",
  "Advanced Vinyasa-Krama Yoga Teacher",
  "Disciple of Srivatsa Ramaswami — 30 years of direct study with Sri T. Krishnamacharya",
  "Lineage of T. Krishnamacharya — guru of B.K.S. Iyengar, Pattabhi Jois, Indra Devi & T.K.V. Desikachar",
];

const TIMES_OF_INDIA_URL =
  "https://timesofindia.indiatimes.com/life-style/health-fitness/weight-loss/weight-loss-story-i-had-a-high-protein-diet-and-stopped-drinking-alcohol-lost-50-kilos/amp_etphotostory/96703829.cms";

export default function AboutFounder() {
  return (
    <section className="section-pad bg-sand-200/50">
      <div className="container-x">
        <div className="grid lg:grid-cols-2 gap-10 md:gap-14 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="rounded-[2rem] overflow-hidden aspect-[4/5] border border-sand-300 shadow-xl bg-sand-200">
              <img src={DHRUVA_IMG} alt="Dhruva Lingappa, Founder of Weight Loss Gurukul" className="h-full w-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-5 shadow-xl border border-sand-300 max-w-[280px] hidden md:block">
              <Sparkles className="h-5 w-5 text-clay" />
              <p className="mt-2 text-sm font-serif italic text-forest leading-snug">
                "Anyone can achieve identical results — with dedication, determination &amp; discipline."
              </p>
              <p className="mt-1 text-xs text-ink-muted">— Dhruva Lingappa</p>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <p className="eyebrow">Meet your coach</p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl md:text-5xl text-forest leading-tight">
              Dhruva Lingappa — India's leading <em className="italic text-clay">transformation maestro.</em>
            </h2>
            <p className="mt-5 text-ink-soft leading-relaxed">
              Dhruva personally guided himself from morbid obesity (40+ BMI) and a hypothyroid condition to a 110 lb (50 kg) loss — and has remained off thyroid medication since 2011. After years of failed diets and gym memberships, the breakthrough came not from modern fitness, but from ancient wisdom practised as one unified system: Yogasana, Pranayama, Meditation, Yoga Nidra, mindful nutrition, and habit mastery.
            </p>
            <p className="mt-4 text-ink-soft leading-relaxed">
              Today, that exact methodology lives on at Weight Loss Gurukul — private 1-on-1 coaching that brings the ancient Gurukul tradition home to you.
            </p>

            {/* Featured in */}
            <a
              href={TIMES_OF_INDIA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-toi-link"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-sand-300 bg-white/80 px-4 py-2 text-xs text-ink-soft hover:border-forest/40 hover:text-forest transition-colors"
            >
              <Award className="h-3.5 w-3.5 text-clay" />
              Featured in <strong className="text-forest font-medium">The Times of India</strong>
              <ExternalLink className="h-3 w-3" />
            </a>

            <div className="mt-8 md:mt-10 flex flex-wrap gap-3">
              <a
                href={waLink(WA_MESSAGES.trial)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-whatsapp-cta"
                className="btn-accent"
              >
                <MessageCircle className="h-4 w-4" /> Chat with Dhruva
              </a>
              <Link to="/about" data-testid="about-learn-more" className="btn-outline">
                Full story <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Before / After transformation panel */}
        <div className="mt-16 md:mt-20 rounded-[2rem] overflow-hidden border border-sand-300 bg-white p-6 md:p-10">
          <div className="grid lg:grid-cols-3 gap-8 md:gap-10 items-center">
            <div className="lg:col-span-1">
              <p className="eyebrow">Living proof</p>
              <h3 className="mt-3 font-serif text-2xl md:text-3xl text-forest leading-tight">
                264 lbs – 154 lbs<br/>
                <span className="text-base md:text-lg text-clay font-medium not-italic">(Lost 110 lbs)</span><br/>
                <span className="italic text-clay">120 kgs – 70 kgs</span><br/>
                <span className="text-base md:text-lg text-clay font-medium not-italic">(Lost 50 kgs)</span>
              </h3>
              <p className="mt-4 text-sm text-ink-soft leading-relaxed">
                Dhruva's own transformation — and the foundation of every Gurukul program. The protocol you'll follow is the one he engineered on himself, refined over a decade of practice in the Krishnamacharya tradition.
              </p>
              <ul className="mt-5 space-y-2">
                {CREDENTIALS.map((c) => (
                  <li key={c} className="flex items-start gap-2 text-xs text-ink-soft leading-relaxed">
                    <CheckCircle2 className="h-4 w-4 text-forest shrink-0 mt-0.5" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-2">
              <div className="rounded-2xl overflow-hidden border border-sand-300 bg-sand-100">
                <img
                  src={BEFORE_AFTER}
                  alt="Before and after transformation of Dhruva Lingappa — 264 lbs (120 kg) to 154 lbs (70 kg)"
                  className="w-full h-auto block min-h-[260px] sm:min-h-[360px] md:min-h-[440px] object-contain"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
